// Stammdaten, Status und Löschprüfung der Eigentümer (Handoff Eigentümer §7, Screens 36, 37).
// Reine Funktionen – testbar. Firma/Person nur über lib/eigentuemer/anzeige.ts.

import { emailGueltig } from '../immobilien/portal';
import { vortag } from '../mieter/mietstaende';
import { istFirma, type EigentuemerNameFelder } from './anzeige';

export type EigAnredeWahl = 'Frau' | 'Herr' | 'Eheleute' | 'Firma' | '';

export interface EigStammEntwurf {
  anrede: EigAnredeWahl;
  firma: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  steuerId: string;
  steuerNr: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  stadt: string;
  notizen: string;
}

const t = (v: unknown): string => (typeof v === 'string' ? v.trim() : v === null || v === undefined ? '' : String(v).trim());

/** Bisherige Anrede (auch „Divers“ oder Briefanrede) → Auswahl; Firma über anzeige.ts. */
export function anredeWahl(e: EigentuemerNameFelder): EigAnredeWahl {
  if (istFirma(e)) return 'Firma';
  const a = t(e.anrede).toLowerCase();
  if (/\beheleute\b/.test(a)) return 'Eheleute';
  if (/\bfrau\b/.test(a) && !/\bherr\b/.test(a)) return 'Frau';
  if (/\bherr\b/.test(a) && !/\bfrau\b/.test(a)) return 'Herr';
  return '';
}

export function stammAusEigentuemer(e: EigentuemerNameFelder & Record<string, unknown>): EigStammEntwurf {
  return {
    anrede: anredeWahl(e), firma: t(e.firma_name), vorname: t(e.vorname), nachname: t(e.nachname),
    email: t(e.email), telefon: t(e.telefon) || t(e.mobil), steuerId: t(e.steuer_id), steuerNr: t(e.steuer_nr),
    strasse: t(e.strasse), hausnummer: t(e.hausnummer), plz: t(e.plz), stadt: t(e.stadt), notizen: t(e.notizen),
  };
}

export type StammFeld = 'name' | 'firma' | 'email' | 'steuerId' | 'plz';

export function stammFehler(e: EigStammEntwurf): Partial<Record<StammFeld, string>> {
  const f: Partial<Record<StammFeld, string>> = {};
  if (e.anrede === 'Firma') { if (!e.firma.trim()) f.firma = 'Firmenname fehlt'; }
  else if (!e.nachname.trim() && !e.firma.trim()) f.name = 'Nachname fehlt';
  if (e.email.trim() && !emailGueltig(e.email)) f.email = 'Bitte eine gültige E-Mail-Adresse eingeben';
  const sid = e.steuerId.replace(/\s+/g, '');
  if (sid && !/^\d{11}$/.test(sid)) f.steuerId = 'Steuer-ID hat 11 Ziffern';
  if (e.plz.trim() && !/^\d{4,5}$/.test(e.plz.trim())) f.plz = 'PLZ prüfen';
  return f;
}

export function stammGeaendert(a: EigStammEntwurf, b: EigStammEntwurf): boolean {
  return (Object.keys(a) as (keyof EigStammEntwurf)[]).some(k => a[k].trim() !== b[k].trim());
}

/**
 * Update für `eigentuemer` (E-Mail/Telefon laufen über kontakt_kanaele). Eine leere Anrede
 * überschreibt eine bisherige Freitext-Anrede nicht.
 */
export function stammPayload(e: EigStammEntwurf, start: EigStammEntwurf): Record<string, string | null> {
  const leer = (s: string): string | null => (s.trim() ? s.trim() : null);
  const p: Record<string, string | null> = {
    firma_name: e.anrede === 'Firma' || e.firma.trim() ? leer(e.firma) : null,
    vorname: e.vorname.trim(), nachname: e.nachname.trim(),
    steuer_id: leer(e.steuerId.replace(/\s+/g, '')), steuer_nr: leer(e.steuerNr),
    strasse: leer(e.strasse), hausnummer: leer(e.hausnummer), plz: leer(e.plz), stadt: leer(e.stadt), notizen: leer(e.notizen),
  };
  // nur bei Änderung – sonst bliebe von „Sehr geehrte Eheleute Bender und Hahn“ nur „Eheleute“
  if (e.anrede !== start.anrede) p.anrede = e.anrede || null;
  return p;
}

/** Inaktiv: Status setzen, ein offenes Vertragsende auf heute. */
export function inaktivPatch(vertragsende: string | null | undefined, heute: string): Record<string, string> {
  return { status: 'Inaktiv', ...(vertragsende ? {} : { vertragsende: heute }) };
}

/** Wieder aktiv: Status setzen; ein bereits erreichtes Vertragsende wird entfernt. */
export function aktivPatch(vertragsende: string | null | undefined, heute: string): Record<string, string | null> {
  const ende = t(vertragsende).slice(0, 10);
  return { status: 'Aktiv', ...(ende && ende <= heute ? { vertragsende: null } : {}) };
}

// ── Löschprüfung (wie EigentuemerLoeschenFormular) ─────────────────────────────

export interface LoeschZuordnung { objektId: string; objektName: string; detail: string }

export interface LoeschPruefung {
  /** Objekte über objekte.eigentuemer_id, entity_links (eigentum) und objekt_eigentuemer */
  objekte: LoeschZuordnung[];
  /** Zeilen in einheit_eigentuemer (auch beendete – Historie) */
  einheiten: number;
  /** analysierte E-Mails (werden beim Löschen entkoppelt, blockieren nicht) */
  emails: number;
}

export function loeschenMoeglich(p: LoeschPruefung): boolean {
  return p.objekte.length === 0 && p.einheiten === 0;
}

/** Text für „Löschen nicht möglich“ (Screen 37). */
export function loeschText(name: string, p: LoeschPruefung): string {
  const n = p.objekte.length || (p.einheiten > 0 ? 1 : 0);
  const mails = p.emails ? ` und mit ${p.emails} analysierten ${p.emails === 1 ? 'E-Mail' : 'E-Mails'} verknüpft` : '';
  return `${name} ist noch ${n} ${n === 1 ? 'Objekt' : 'Objekten'} zugeordnet (objekt_eigentuemer / einheit_eigentuemer)${mails}. `
    + 'Erst die Zuordnungen aufheben oder den Eigentümer auf inaktiv setzen – dann bleibt die Historie erhalten.';
}

/** Letzter Tag beim Aufheben einer Zuweisung: gestern = ab heute ehemalig. */
export function aufhebenBisVorschlag(heute: string): string {
  return vortag(heute);
}

/** Eigentümer inaktiv/ehemalig gesetzt? (wie die Liste) */
export function istInaktiv(status: unknown): boolean {
  const s = typeof status === 'string' ? status.trim().toLowerCase() : '';
  return s === 'inaktiv' || s === 'ehemalig';
}
